import React, { useMemo, useRef, useState } from 'react';
import { Container } from 'react-bootstrap';
import SearchControls from './components/SearchControls';
import NgramChartRecharts from './components/NgramChartRecharts';
import AppHeader from './components/AppHeader';
import { fetchNgramData, MIN_YEAR, MAX_YEAR } from './services/ngramProcessor';
import 'bootstrap/dist/css/bootstrap.min.css';
import './App.css';

function App() {
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [graphType, setGraphType] = useState('relative');
    const [settings, setSettings] = useState({
        capitalization: false,
        smoothing: 4,
        lineThickness: 3,
        scaling: 'auto',
        relativeNormalization: 'standard',
        curvePattern: false,
        curvePatternColorMode: 'black',
        zoomStart: MIN_YEAR,
        zoomEnd: MAX_YEAR
    });
    const [corpus, setCorpus] = useState('');
    const [lastQuery, setLastQuery] = useState({
        words: [],
        corpus: 'bok',
        lang: 'nob',
        graphType: 'relative'
    });
    const lastRequestKeyRef = useRef('');
    const shareUrl = useMemo(() => {
        const params = new URLSearchParams({
            terms: (lastQuery?.words || []).join(','),
            mode: lastQuery?.graphType || 'relative',
            corpus: lastQuery?.corpus || 'bok',
            lang: lastQuery?.lang || 'nob',
            case: settings?.capitalization ? '1' : '0',
            smooth: String(settings?.smoothing ?? 3),
            scale: String(settings?.scaling ?? 'auto'),
            relNorm: String(settings?.relativeNormalization ?? 'standard'),
            pattern: settings?.curvePattern ? '1' : '0',
            patternColor: String(settings?.curvePatternColorMode ?? 'black'),
            from: String(settings?.zoomStart ?? MIN_YEAR),
            to: String(settings?.zoomEnd ?? MAX_YEAR)
        });

        const url = new URL(window.location.href);
        url.hash = `v2?${params.toString()}`;
        return url.toString();
    }, [lastQuery, settings]);

    const handleSearch = async (words, corpus, lang, graphType) => {
        const requestKey = JSON.stringify({
            words,
            corpus,
            lang,
            graphType,
            capitalization: settings?.capitalization ?? false,
            relativeNormalization: settings?.relativeNormalization ?? 'standard'
        });

        if (lastRequestKeyRef.current === requestKey) {
            return;
        }

        lastRequestKeyRef.current = requestKey;
        setError(null);
        setLastQuery({
            words,
            corpus,
            lang,
            graphType
        });
        try {
            const result = await fetchNgramData(words, corpus, lang, graphType, settings);
            setData(result);
            setCorpus(corpus);
        } catch (err) {
            // Allow retry of same query if the previous attempt failed.
            lastRequestKeyRef.current = '';
            setError(err.message);
        }
    };

    const handleSettingsChange = (nextSettings) => {
        setSettings((prev) => ({
            ...prev,
            ...nextSettings
        }));
    };

    return (
        <>
            <AppHeader data={data} shareUrl={shareUrl} />
            <Container fluid className="app-main-content">
            <div className="app-search-row mb-4">
                    <SearchControls 
                        onSearch={handleSearch}
                        onGraphTypeChange={setGraphType}
                        data={data}
                        settings={settings}
                        onSettingsChange={handleSettingsChange}
                    />
            </div>
                    
            <div className="chart-container" style={{ height: 'calc(100vh - 120px)' }}>
                        {error && <div className="text-danger">{error}</div>}
                {data && <NgramChartRecharts 
                    data={data} 
                    graphType={graphType}
                    settings={settings}
                    corpus={corpus}
                    onSettingsChange={handleSettingsChange}
                />}
                </div>
            </Container>
        </>
    );
}

export default App; 